import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Zap, ArrowLeft, Mail, KeyRound, Lock } from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import { authApi } from '../api/client';
import { useAuthStore } from '../store/useAuthStore';
import LogoWordmark from '../components/LogoWordmark';
import './AuthForm.css';

const HAS_GOOGLE   = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
const HAS_FACEBOOK = !!process.env.REACT_APP_FACEBOOK_APP_ID;

// ── Composant saisie code OTP (6 cases) ─────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const inputs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = digits.map((d, j) => (j === i ? '' : d)).join('');
      onChange(next);
      if (i > 0) inputs.current[i - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) { inputs.current[i - 1]?.focus(); return; }
    if (e.key === 'ArrowRight' && i < 5) { inputs.current[i + 1]?.focus(); return; }
  };

  const handleChange = (i, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const next = digits.map((d, j) => (j === i ? char : d)).join('');
    onChange(next);
    if (i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) { onChange(pasted.padEnd(6, '')); inputs.current[Math.min(pasted.length, 5)]?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="af-otp-wrapper">
      <div className="af-otp-label">Entrez votre code à 6 chiffres</div>
      <div className="af-otp" onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={el => inputs.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            placeholder="·"
            onChange={e => handleChange(i, e)}
            onKeyDown={e => handleKey(i, e)}
            onFocus={e => e.target.select()}
            disabled={disabled}
            className={`af-otp-cell${d ? ' af-otp-cell--filled' : ''}`}
            autoComplete="one-time-code"
          />
        ))}
      </div>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────
export default function AuthForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname ?? '/expert';

  const [tab, setTab]             = useState('login');
  const [loading, setLoading]     = useState(false);
  const [socialPending, setSocial]= useState(null);
  const [msg, setMsg]             = useState({ text: '', type: '' });

  // ── Reset flow : 'idle' | 'email' | 'code' | 'password' | 'done'
  const [resetStep, setResetStep] = useState('idle');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode,  setResetCode]  = useState('');
  const [newPwd,     setNewPwd]     = useState('');
  const [newPwdConf, setNewPwdConf] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [regData,   setRegData]   = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
  });

  const showMsg  = (text, type = 'error') => setMsg({ text, type });
  const clearMsg = () => setMsg({ text: '', type: '' });

  const afterLogin = (resp) => {
    const payload = resp?.data ?? resp;
    useAuthStore.login(payload.user, payload);
    navigate(from, { replace: true });
  };

  // ── Google ───────────────────────────────────────────────────────────────
  const handleGoogleToken = useCallback(async (response) => {
    setSocial('google');
    try {
      const { data } = await authApi.googleLogin({ access_token: response.credential });
      afterLogin(data);
    } catch {
      showMsg('Erreur lors de la connexion Google.');
    } finally {
      setSocial(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => window.google?.accounts.id.initialize({ client_id: clientId, callback: handleGoogleToken });
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch {} };
  }, [handleGoogleToken]);

  // ── Facebook ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    if (!appId || document.getElementById('fb-sdk')) return;
    window.fbAsyncInit = () => window.FB?.init({ appId, cookie: true, xfbml: false, version: 'v18.0' });
    const s = document.createElement('script');
    s.id = 'fb-sdk'; s.src = 'https://connect.facebook.net/fr_FR/sdk.js';
    s.async = true; s.defer = true;
    document.body.appendChild(s);
  }, []);

  const handleFacebookClick = () => {
    window.FB?.login(async (res) => {
      if (!res.authResponse) return;
      setSocial('facebook');
      try {
        const { data } = await authApi.facebookLogin({ access_token: res.authResponse.accessToken });
        afterLogin(data);
      } catch {
        showMsg('Erreur lors de la connexion Facebook.');
      } finally {
        setSocial(null);
      }
    }, { scope: 'email,public_profile' });
  };

  // ── Connexion ────────────────────────────────────────────────────────────
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    clearMsg(); setLoading(true);
    try {
      const { data } = await authApi.login(loginData);
      afterLogin(data);
    } catch (err) {
      showMsg(
        err.response?.data?.message ||
        err.response?.data?.detail  ||
        err.response?.data?.error   ||
        'Identifiants invalides.',
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Inscription ──────────────────────────────────────────────────────────
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (regData.password !== regData.confirmPassword) { showMsg('Les mots de passe ne correspondent pas.'); return; }
    if (regData.password.length < 8)                  { showMsg('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    clearMsg(); setLoading(true);
    try {
      await authApi.signup({
        firstName: regData.firstName, lastName: regData.lastName,
        email: regData.email, password: regData.password, repassword: regData.confirmPassword,
      });
      showMsg('Compte créé ! Vérifiez votre email pour l\'activer.', 'success');
      setTimeout(() => { setTab('login'); clearMsg(); }, 3500);
    } catch (err) {
      showMsg(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de l\'inscription.');
    } finally {
      setLoading(false);
    }
  };

  // ── Reset — étape 1 : envoi code ─────────────────────────────────────────
  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!resetEmail) { showMsg('Saisissez votre adresse email.'); return; }
    clearMsg(); setLoading(true);
    try {
      await authApi.passwordReset({ email: resetEmail });
      showMsg('Code envoyé ! Vérifiez votre boîte email.', 'success');
      setTimeout(() => { clearMsg(); setResetStep('code'); }, 1200);
    } catch (err) {
      showMsg(err.response?.data?.message || err.response?.data?.error || 'Impossible d\'envoyer le code.');
    } finally {
      setLoading(false);
    }
  };

  // ── Reset — étape 2 : vérification code ─────────────────────────────────
  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (resetCode.replace(/\D/g, '').length < 6) { showMsg('Saisissez les 6 chiffres du code.'); return; }
    clearMsg();
    setResetStep('password');
  };

  // ── Reset — étape 3 : nouveau mot de passe ───────────────────────────────
  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPwd !== newPwdConf) { showMsg('Les mots de passe ne correspondent pas.'); return; }
    if (newPwd.length < 8)    { showMsg('Au moins 8 caractères requis.'); return; }
    clearMsg(); setLoading(true);
    try {
      await authApi.passwordResetConfirm({
        email:        resetEmail,
        code:         resetCode.replace(/\D/g, ''),
        new_password: newPwd,
      });
      showMsg('Mot de passe modifié avec succès !', 'success');
      setResetStep('done');
      setTimeout(() => {
        setResetStep('idle'); setResetEmail(''); setResetCode('');
        setNewPwd(''); setNewPwdConf(''); clearMsg(); setTab('login');
      }, 2500);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Code incorrect ou expiré.';
      showMsg(msg);
      // Si code expiré, retour à l'étape code
      if (err.response?.status === 400) setResetStep('code');
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetEmail(loginData.email || '');
    setResetCode(''); setNewPwd(''); setNewPwdConf('');
    clearMsg(); setResetStep('email');
  };

  const closeReset = () => {
    setResetStep('idle'); clearMsg();
  };

  const switchTab = (t) => { setTab(t); clearMsg(); setResetStep('idle'); };
  const busy = loading || !!socialPending;
  const isReset = resetStep !== 'idle';

  return (
    <div className="af">
      <div className="af-orb af-orb--1" />
      <div className="af-orb af-orb--2" />
      <div className="af-orb af-orb--3" />

      <div className="af-card">

        {/* ── Brand ── */}
        <Link to="/" className="af-brand">
          <span className="af-brand-icon"><Zap size={18} /></span>
          <LogoWordmark height={27} />
          <span className="badge-beta">BETA</span>
        </Link>
        <p className="af-tagline">Plateforme de gestion géodésique</p>

        {/* ── Panneau reset mot de passe (overlay sur le formulaire) ── */}
        {isReset && (
          <div className="af-reset-panel">

            {/* Header reset */}
            <div className="af-reset-header">
              {resetStep !== 'done' && (
                <button className="af-reset-back" onClick={closeReset} type="button">
                  <ArrowLeft size={15} />
                </button>
              )}
              <div className="af-reset-steps">
                {['email','code','password'].map((s, i) => (
                  <span
                    key={s}
                    className={`af-reset-step-dot${
                      resetStep === s ? ' af-reset-step-dot--active' :
                      ['email','code','password'].indexOf(resetStep) > i ? ' af-reset-step-dot--done' : ''
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Message */}
            {msg.text && (
              <div className={`af-msg af-msg--${msg.type}`} role="alert">
                <span>{msg.text}</span>
                <button onClick={clearMsg} aria-label="Fermer">×</button>
              </div>
            )}

            {/* ── Étape 1 : email ── */}
            {resetStep === 'email' && (
              <form onSubmit={handleResetRequest} className="af-reset-step" noValidate>
                <div className="af-reset-icon"><Mail size={22} /></div>
                <h3 className="af-reset-title">Réinitialiser le mot de passe</h3>
                <p className="af-reset-desc">
                  Saisissez votre email. Nous vous enverrons un code à 6 chiffres valable 10 minutes.
                </p>
                <div className="af-field">
                  <label htmlFor="rst-email">Adresse email</label>
                  <input
                    id="rst-email" type="email" autoComplete="email"
                    placeholder="votre@email.com"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    disabled={busy} required autoFocus
                  />
                </div>
                <button type="submit" className="af-submit" disabled={busy || !resetEmail}>
                  {loading && <span className="af-spinner" />}
                  {loading ? 'Envoi…' : 'Envoyer le code'}
                </button>
              </form>
            )}

            {/* ── Étape 2 : code OTP ── */}
            {resetStep === 'code' && (
              <form onSubmit={handleCodeSubmit} className="af-reset-step" noValidate>
                <div className="af-reset-icon af-reset-icon--cyan"><KeyRound size={22} /></div>
                <h3 className="af-reset-title">Saisir le code</h3>
                <p className="af-reset-desc">
                  Entrez le code à 6 chiffres envoyé à <strong>{resetEmail}</strong>.
                </p>
                <OtpInput value={resetCode} onChange={setResetCode} disabled={busy} />
                <button
                  type="submit"
                  className="af-submit"
                  disabled={busy || resetCode.replace(/\D/g,'').length < 6}
                >
                  Valider le code
                </button>
                <button
                  type="button"
                  className="af-link-btn"
                  onClick={() => { clearMsg(); setResetStep('email'); }}
                  disabled={busy}
                >
                  Renvoyer un code
                </button>
              </form>
            )}

            {/* ── Étape 3 : nouveau mot de passe ── */}
            {resetStep === 'password' && (
              <form onSubmit={handleNewPasswordSubmit} className="af-reset-step" noValidate>
                <div className="af-reset-icon af-reset-icon--violet"><Lock size={22} /></div>
                <h3 className="af-reset-title">Nouveau mot de passe</h3>
                <p className="af-reset-desc">Choisissez un mot de passe sécurisé (min. 8 caractères).</p>
                <div className="af-field">
                  <label htmlFor="rst-pwd">Nouveau mot de passe</label>
                  <input
                    id="rst-pwd" type="password" autoComplete="new-password"
                    placeholder="Min. 8 caractères"
                    value={newPwd}
                    onChange={e => setNewPwd(e.target.value)}
                    disabled={busy} required autoFocus
                  />
                </div>
                <div className="af-field">
                  <label htmlFor="rst-cpwd">Confirmer</label>
                  <input
                    id="rst-cpwd" type="password" autoComplete="new-password"
                    placeholder="••••••••"
                    value={newPwdConf}
                    onChange={e => setNewPwdConf(e.target.value)}
                    disabled={busy} required
                  />
                </div>
                <button
                  type="submit"
                  className="af-submit"
                  disabled={busy || !newPwd || !newPwdConf}
                >
                  {loading && <span className="af-spinner" />}
                  {loading ? 'Modification…' : 'Modifier le mot de passe'}
                </button>
              </form>
            )}

            {/* ── Done ── */}
            {resetStep === 'done' && (
              <div className="af-reset-step af-reset-done">
                <div className="af-reset-done-icon">✓</div>
                <h3 className="af-reset-title">Mot de passe modifié !</h3>
                <p className="af-reset-desc">Vous allez être redirigé vers la connexion…</p>
              </div>
            )}
          </div>
        )}

        {/* ── Tabs (masqués pendant le reset) ── */}
        {!isReset && (
          <>
            <div className="af-tabs" role="tablist">
              <button
                role="tab" aria-selected={tab === 'login'}
                className={`af-tab${tab === 'login' ? ' af-tab--active' : ''}`}
                onClick={() => switchTab('login')}
              >Connexion</button>
              <button
                role="tab" aria-selected={tab === 'register'}
                className={`af-tab${tab === 'register' ? ' af-tab--active' : ''}`}
                onClick={() => switchTab('register')}
              >Inscription</button>
              <span className={`af-tab-slider af-tab-slider--${tab}`} aria-hidden />
            </div>

            {msg.text && (
              <div className={`af-msg af-msg--${msg.type}`} role="alert">
                <span>{msg.text}</span>
                <button onClick={clearMsg} aria-label="Fermer">×</button>
              </div>
            )}

            {/* ════ CONNEXION ════ */}
            {tab === 'login' && (
              <div className="af-section">
                <form onSubmit={handleLoginSubmit} className="af-form" noValidate>
                  <div className="af-field">
                    <label htmlFor="l-email">Email</label>
                    <input
                      id="l-email" type="email" autoComplete="email"
                      placeholder="votre@email.com"
                      value={loginData.email}
                      onChange={e => setLoginData(p => ({ ...p, email: e.target.value }))}
                      disabled={busy} required
                    />
                  </div>
                  <div className="af-field">
                    <label htmlFor="l-pwd">
                      Mot de passe
                      <button type="button" className="af-forgot" onClick={openReset} disabled={busy}>
                        Oublié ?
                      </button>
                    </label>
                    <input
                      id="l-pwd" type="password" autoComplete="current-password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))}
                      disabled={busy} required
                    />
                  </div>
                  <button type="submit" className="af-submit" disabled={busy}>
                    {loading && <span className="af-spinner" />}
                    {loading ? 'Connexion…' : 'Se connecter'}
                  </button>
                </form>

                {(HAS_GOOGLE || HAS_FACEBOOK) && (<>
                  <div className="af-divider"><span>ou continuer avec</span></div>
                  <div className="af-social">
                    {HAS_GOOGLE && (
                      <button className="af-social-btn af-social-btn--google" onClick={() => window.google?.accounts.id.prompt()} disabled={busy} type="button">
                        {socialPending === 'google' ? <span className="af-spinner af-spinner--dark" /> : <FcGoogle size={20} />}
                        Google
                      </button>
                    )}
                    {HAS_FACEBOOK && (
                      <button className="af-social-btn af-social-btn--facebook" onClick={handleFacebookClick} disabled={busy} type="button">
                        {socialPending === 'facebook' ? <span className="af-spinner af-spinner--dark" /> : <FaFacebook size={20} color="#1877f2" />}
                        Facebook
                      </button>
                    )}
                  </div>
                </>)}
              </div>
            )}

            {/* ════ INSCRIPTION ════ */}
            {tab === 'register' && (
              <div className="af-section">
                <form onSubmit={handleRegisterSubmit} className="af-form" noValidate>
                  <div className="af-row">
                    <div className="af-field">
                      <label htmlFor="r-fn">Prénom</label>
                      <input id="r-fn" type="text" autoComplete="given-name" placeholder="Jean"
                        value={regData.firstName} onChange={e => setRegData(p => ({ ...p, firstName: e.target.value }))} disabled={busy} required />
                    </div>
                    <div className="af-field">
                      <label htmlFor="r-ln">Nom</label>
                      <input id="r-ln" type="text" autoComplete="family-name" placeholder="Dupont"
                        value={regData.lastName} onChange={e => setRegData(p => ({ ...p, lastName: e.target.value }))} disabled={busy} required />
                    </div>
                  </div>
                  <div className="af-field">
                    <label htmlFor="r-email">Email</label>
                    <input id="r-email" type="email" autoComplete="email" placeholder="votre@email.com"
                      value={regData.email} onChange={e => setRegData(p => ({ ...p, email: e.target.value }))} disabled={busy} required />
                  </div>
                  <div className="af-field">
                    <label htmlFor="r-pwd">Mot de passe</label>
                    <input id="r-pwd" type="password" autoComplete="new-password" placeholder="Min. 8 caractères"
                      value={regData.password} onChange={e => setRegData(p => ({ ...p, password: e.target.value }))} disabled={busy} required />
                  </div>
                  <div className="af-field">
                    <label htmlFor="r-cpwd">Confirmer</label>
                    <input id="r-cpwd" type="password" autoComplete="new-password" placeholder="••••••••"
                      value={regData.confirmPassword} onChange={e => setRegData(p => ({ ...p, confirmPassword: e.target.value }))} disabled={busy} required />
                  </div>
                  <button type="submit" className="af-submit" disabled={busy}>
                    {loading && <span className="af-spinner" />}
                    {loading ? 'Inscription…' : 'Créer mon compte'}
                  </button>
                  <p className="af-terms">En vous inscrivant, vous acceptez nos <a href="#terms" className="af-link">conditions d'utilisation</a>.</p>
                </form>

                {(HAS_GOOGLE || HAS_FACEBOOK) && (<>
                  <div className="af-divider"><span>ou s'inscrire avec</span></div>
                  <div className="af-social">
                    {HAS_GOOGLE && (
                      <button className="af-social-btn af-social-btn--google" onClick={() => window.google?.accounts.id.prompt()} disabled={busy} type="button">
                        {socialPending === 'google' ? <span className="af-spinner af-spinner--dark" /> : <FcGoogle size={20} />}
                        Google
                      </button>
                    )}
                    {HAS_FACEBOOK && (
                      <button className="af-social-btn af-social-btn--facebook" onClick={handleFacebookClick} disabled={busy} type="button">
                        {socialPending === 'facebook' ? <span className="af-spinner af-spinner--dark" /> : <FaFacebook size={20} color="#1877f2" />}
                        Facebook
                      </button>
                    )}
                  </div>
                </>)}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
